import { createFileRoute, Link } from '@tanstack/react-router';
import { UserTags } from '~/partials/Tags';
import { Separator } from '~/primitives/separator';
import { useState } from 'react';
import { ExternalLink, Info } from 'lucide-react';
import { EditRecordSheet } from './-EditRecordSheet';
import {
    cn,
    formatLevelGDID,
    getCompletionTime,
    getLevelDisplayName,
    getLevelDisplayPrefix,
    seo,
} from '~/util';
import { recordQuery } from '~/util/queries/staff/submissions';
import { Button } from '~/primitives/button';
import TooltipButton from '~/partials/Tooltip';
import { SiDiscord } from '@icons-pack/react-simple-icons';
import { Checkbox } from '~/primitives/checkbox';
import { useLocaleDateTimeString } from '~/util/hooks/useLocale';
import { VideoPlayer } from '~/components/VideoPlayer';
import { useAuthData } from '~/components/Providers/AuthProvider';

export const Route = createFileRoute(
    '/_authenticated/staff/_recordsLayout/records/$recordid',
)({
    loaderDeps: ({ search: { list } }) => ({ list }),
    loader: async ({
        context: { queryClient },
        params: { recordid },
        deps: { list },
    }) =>
        await queryClient.fetchQuery(
            recordQuery({
                list,
                id: recordid,
            }),
        ),
    head: ({ loaderData: record }) => {
        return {
            meta: seo({
                title: record
                    ? `${getLevelDisplayName(record.level)} - ${record?.submitted_by.global_name}`
                    : 'Unknown Record',
            }),
        };
    },
    component: Record,
});

function Record() {
    const { id } = useAuthData() ?? {};

    const record = Route.useLoaderData();
    const [editMode, setEditMode] = useState(false);
    const localeDateTime = useLocaleDateTimeString();

    const isOwnRecord = id === record.submitted_by.id;
    return (
        <>
            {isOwnRecord && (
                <div
                    className={cn(
                        'flex justify-between items-center gap-2 text-white mt-5 px-2 py-2 border-2 rounded-lg bg-destructive/30 border-destructive',
                    )}
                >
                    <p>
                        <Info className="inline size-6 mr-2" />
                        You cannot edit your own record.
                    </p>
                </div>
            )}
            <div className="flex flex-col my-4 gap-4">
                <div className="flex flex-col xl:flex-row w-full items-center justify-center gap-4">
                    <div className="w-full xl:w-1/2 h-full rounded-xl">
                        <VideoPlayer videoUrl={record.video_url} />
                    </div>
                    <div className="w-full xl:w-1/3 xl:border-l-2 xl:border-secondary-light p-2 xl:pl-6 xl:ml-6">
                        <h2 className="text-3xl font-semibold leading-tight">
                            <span className="text-secondary-light">
                                {getLevelDisplayPrefix(record.level)}
                            </span>{' '}
                            <Link
                                to="/list/$levelid"
                                search={({ list }) => ({ list })}
                                params={{
                                    levelid: formatLevelGDID(record.level),
                                }}
                                target="_blank"
                            >
                                {record.level.name}
                            </Link>
                        </h2>
                        <div className="flex flex-col">
                            <h3 className="font-bold">Submitted by:</h3>
                            <div className="flex items-center gap-2">
                                <UserTags
                                    highContrast
                                    item={record.submitted_by}
                                    showFullName
                                />
                                {record.submitted_by.discord_id && (
                                    <TooltipButton
                                        text={`Open discord profile (might not work depending on your OS or their privacy settings). Discord ID: ${record.submitted_by.discord_id}`}
                                        side="top"
                                    >
                                        <a
                                            href={`discord://-/users/${record.submitted_by.discord_id}`}
                                        >
                                            <SiDiscord className="hover:text-secondary-light transition-colors" />
                                        </a>
                                    </TooltipButton>
                                )}
                            </div>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex flex-col xl:flex-row xl:justify-between">
                            <div>
                                <div className="flex gap-8">
                                    <div className="flex flex-col">
                                        <h3 className="font-bold">Device:</h3>
                                        <p>{record.mobile ? 'Mobile' : 'PC'}</p>
                                    </div>
                                    {record.completion_time !== undefined && (
                                        <div className="flex flex-col">
                                            <h3 className="font-bold">
                                                Completion time:
                                            </h3>
                                            <p>
                                                {getCompletionTime(
                                                    record.completion_time,
                                                )}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col">
                                    <h3 className="font-bold">
                                        Completion link:
                                    </h3>
                                    <a
                                        href={record.video_url ?? '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 underline break-all hover:text-blue-700"
                                    >
                                        {record.video_url ?? 'N/A'}
                                    </a>
                                </div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold">Verification:</h3>
                                    <Checkbox
                                        checked={record.is_verification}
                                        inert
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-bold">Hidden video:</h3>
                                    <Checkbox
                                        checked={record.hide_video}
                                        inert
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <h3 className="font-bold">Achieved on:</h3>
                                    {localeDateTime(record.achieved_at)}
                                </div>
                            </div>
                        </div>
                        <Separator className="my-2" />
                        <div className="flex flex-col gap-2">
                            <Button
                                className="w-full"
                                onClick={() => setEditMode(true)}
                            >
                                Edit Record
                            </Button>
                            <Link
                                to="/staff/submissions/$submissionid"
                                params={{
                                    submissionid: record.submission_id,
                                }}
                                search={({ list }) => ({ list })}
                                target="_blank"
                            >
                                <Button className="w-full" variant="secondary">
                                    Open corresponding submission{' '}
                                    <ExternalLink />{' '}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <EditRecordSheet open={editMode} onOpenChange={setEditMode} />
        </>
    );
}
